import React from "react";
import PropTypes from "prop-types";
import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { hasMenuAccess, getMenuAccessInfo } from "../../../utils/menuAccess";

const SidebarMenu = ({
  open,
  Menus,
  selectedMenu,
  selectedSubmenu,
  expandedMenu,
  handleToggleMenu,
  handleSubmenuClick,
  theme,
  marketplacePlans = [],
  userData = null,
}) => {
  return (
    <List
      sx={{
        padding: "12px 16px",
        "& .MuiListItemButton-root": {
          borderRadius: "12px",
          mb: 1,
          justifyContent: open ? "initial" : "center",
          px: 2,
          py: 1.5,
          position: "relative",
          overflow: "hidden",
          "&.Mui-selected": {
            background: "linear-gradient(135deg, rgba(255, 112, 67, 0.1) 0%, rgba(255, 112, 67, 0.05) 100%)",
            color: theme.palette.primary.main,
            borderLeft: `4px solid ${theme.palette.primary.main}`,
            fontWeight: 600,
            boxShadow: "0 2px 8px rgba(255, 112, 67, 0.15)",
            "& .MuiListItemIcon-root": {
              color: theme.palette.primary.main,
              transform: "scale(1.1)",
            },
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "linear-gradient(90deg, rgba(255, 112, 67, 0.08) 0%, transparent 100%)",
              pointerEvents: "none",
            },
          },
          "&:hover": {
            background: "linear-gradient(135deg, rgba(255, 112, 67, 0.06) 0%, rgba(255, 112, 67, 0.02) 100%)",
            transform: "translateX(4px)",
            boxShadow: "0 4px 12px rgba(255, 112, 67, 0.1)",
            "& .MuiListItemIcon-root": {
              transform: "scale(1.05)",
              color: theme.palette.primary.main,
            },
          },
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        },
        "& .MuiListItemIcon-root": {
          minWidth: 0,
          mr: open ? 2 : "auto",
          justifyContent: "center",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          fontSize: "24px",
        },
        "& .MuiListItemText-primary": {
          opacity: open ? 1 : 0,
          transition: "opacity 0.3s",
          fontWeight: 500,
          fontSize: "0.95rem",
        },
      }}
    >
      {Menus.map((m, index) => {
        const accessInfo = getMenuAccessInfo(index, null, marketplacePlans, userData);
        const hasAccess = accessInfo.hasAccess;
        
        // Determina a mensagem do tooltip baseado na razão do bloqueio
        const getTooltipMessage = (reason) => {
          if (!reason) return "";
          return reason === 'user' 
            ? "Acesso restrito para seu usuário" 
            : "Acesso restrito para sua organização";
        };
        
        return (
        <React.Fragment key={index}>
          <ListItem
            disablePadding
            sx={{
              display: "block",
            }}
          >
            <Tooltip 
              title={getTooltipMessage(accessInfo.reason)}
              arrow
              placement="right"
            >
              <span>
                <ListItemButton
                  onClick={() => hasAccess && handleToggleMenu(m, index)}
                  selected={selectedMenu === index && !m.children}
                  disabled={!hasAccess}
                  sx={{
                    minHeight: 52,
                    justifyContent: open ? "initial" : "center",
                    px: 2,
                    position: "relative",
                    opacity: hasAccess ? 1 : 0.5,
                    cursor: hasAccess ? "pointer" : "not-allowed",
                    "&.Mui-disabled": {
                      opacity: 0.5,
                      "& .MuiListItemIcon-root": {
                        opacity: 0.5,
                      },
                      "& .MuiListItemText-root": {
                        opacity: 0.5,
                      },
                    },
                    "&.Mui-selected": {
                      "&::after": {
                        content: '""',
                        position: "absolute",
                        right: 8,
                        top: "50%",
                        transform: "translateY(-50%)",
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        backgroundColor: theme.palette.primary.main,
                        boxShadow: `0 0 8px ${theme.palette.primary.main}`,
                      },
                    },
                  }}
                >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: open ? 1.5 : "auto",
                  justifyContent: "center",
                  color:
                    selectedMenu === index
                      ? theme.palette.primary.main
                      : "inherit",
                }}
              >
                {m.icon}
              </ListItemIcon>
              <ListItemText
                primary={m.title}
                sx={{
                  opacity: open ? 1 : 0,
                  "& .MuiTypography-root": {
                    fontWeight: selectedMenu === index ? 500 : 400,
                  },
                }}
              />
              {m.children && open && (
                <KeyboardArrowDownIcon
                  sx={{
                    transform:
                      expandedMenu === index ? "rotate(180deg)" : "rotate(0)",
                    transition: "transform 0.2s",
                    color:
                      selectedMenu === index
                        ? theme.palette.primary.main
                        : "inherit",
                  }}
                />
              )}
                </ListItemButton>
              </span>
            </Tooltip>
          </ListItem>

          {m.children && expandedMenu === index && open && (
            <List
              component="div"
              disablePadding
              sx={{
                ml: 2,
                borderLeft: "2px solid rgba(255, 112, 67, 0.1)",
                "& .MuiListItemButton-root": {
                  pl: 3,
                  py: 1,
                  ml: 1,
                  borderRadius: "8px",
                  "&.Mui-selected": {
                    background: "linear-gradient(135deg, rgba(255, 112, 67, 0.08) 0%, rgba(255, 112, 67, 0.03) 100%)",
                    borderLeft: `3px solid ${theme.palette.primary.main}`,
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      left: -2,
                      top: "50%",
                      transform: "translateY(-50%)",
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      backgroundColor: theme.palette.primary.main,
                    },
                  },
                  "&:hover": {
                    background: "linear-gradient(135deg, rgba(255, 112, 67, 0.04) 0%, rgba(255, 112, 67, 0.01) 100%)",
                    transform: "translateX(2px)",
                  },
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                },
              }}
            >
              {m.children.map((child, childIndex) => {
                const childAccessInfo = getMenuAccessInfo(index, childIndex, marketplacePlans, userData);
                const hasChildAccess = childAccessInfo.hasAccess;
                
                return (
                  <Tooltip
                    key={childIndex}
                    title={getTooltipMessage(childAccessInfo.reason)}
                    arrow
                    placement="right"
                  >
                    <span>
                      <ListItemButton
                        onClick={() => hasChildAccess && handleSubmenuClick(index, childIndex)}
                        selected={
                          selectedMenu === index && selectedSubmenu === childIndex
                        }
                        disabled={!hasChildAccess}
                        sx={{
                          opacity: hasChildAccess ? 1 : 0.5,
                          cursor: hasChildAccess ? "pointer" : "not-allowed",
                          "&.Mui-disabled": {
                            opacity: 0.5,
                            "& .MuiListItemIcon-root": {
                              opacity: 0.5,
                            },
                            "& .MuiListItemText-root": {
                              opacity: 0.5,
                            },
                          },
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 0,
                            mr: open ? 1.5 : "auto",
                            color:
                              selectedMenu === index && selectedSubmenu === childIndex
                                ? theme.palette.primary.main
                                : "inherit",
                          }}
                        >
                          {child.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={child.title}
                          sx={{
                            opacity: open ? 1 : 0,
                            "& .MuiTypography-root": {
                              fontSize: "0.9rem",
                              fontWeight:
                                selectedMenu === index &&
                                selectedSubmenu === childIndex
                                  ? 500
                                  : 400,
                            },
                          }}
                        />
                      </ListItemButton>
                    </span>
                  </Tooltip>
                );
              })}
            </List>
          )}
        </React.Fragment>
        );
      })}
    </List>
  );
};

SidebarMenu.propTypes = {
  open: PropTypes.bool.isRequired,
  Menus: PropTypes.array.isRequired,
  selectedMenu: PropTypes.number.isRequired,
  selectedSubmenu: PropTypes.number,
  expandedMenu: PropTypes.number,
  handleToggleMenu: PropTypes.func.isRequired,
  handleSubmenuClick: PropTypes.func.isRequired,
  theme: PropTypes.object.isRequired,
  marketplacePlans: PropTypes.array,
  userData: PropTypes.object,
};

export default SidebarMenu;
